/**
 * useDraggable Hook
 *
 * Enables drag-to-move functionality for the Solomon panel trigger.
 * Persists position to localStorage and constrains to viewport bounds.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

const STORAGE_KEY = 'solomon-trigger-position';
const EDGE_MARGIN = 16; // Minimum distance from viewport edges

interface Position {
  x: number;
  y: number;
}

interface UseDraggableOptions {
  /** Initial position if no stored position exists */
  defaultPosition?: Position;
  /** Whether dragging is enabled */
  enabled?: boolean;
  /** Callback when drag ends */
  onDragEnd?: (position: Position) => void;
}

interface UseDraggableReturn {
  /** Current position (x, y from bottom-right) */
  position: Position;
  /** Whether currently dragging */
  isDragging: boolean;
  /** Props to spread on the draggable element */
  dragProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
    style: React.CSSProperties;
  };
  /** Reset position to default */
  resetPosition: () => void;
}

/**
 * Clear stored position (for debugging/reset)
 */
export function clearStoredPosition(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Load position from localStorage and validate it's within viewport
 */
function loadPosition(): Position | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Position;
      // Validate it's a valid number and within reasonable bounds
      if (
        typeof parsed.x === 'number' &&
        typeof parsed.y === 'number' &&
        // eslint-disable-next-line no-restricted-globals
        !isNaN(parsed.x) &&
        // eslint-disable-next-line no-restricted-globals
        !isNaN(parsed.y) &&
        parsed.x >= 0 &&
        parsed.y >= 0 &&
        parsed.x < window.innerWidth &&
        parsed.y < window.innerHeight
      ) {
        return parsed;
      }
      // Invalid position stored, clear it
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
}

/**
 * Save position to localStorage
 */
function savePosition(position: Position): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Constrain position to viewport bounds (just prevents going off-screen)
 */
function constrainToViewport(position: Position, elementWidth: number, elementHeight: number): Position {
  const maxX = window.innerWidth - elementWidth - EDGE_MARGIN;
  const maxY = window.innerHeight - elementHeight - EDGE_MARGIN;

  return {
    x: Math.max(EDGE_MARGIN, Math.min(position.x, maxX)),
    y: Math.max(EDGE_MARGIN, Math.min(position.y, maxY)),
  };
}

export function useDraggable(options: UseDraggableOptions = {}): UseDraggableReturn {
  const { defaultPosition = { x: 24, y: 24 }, enabled = true, onDragEnd } = options;

  // Load initial position from localStorage or use default
  const [position, setPosition] = useState<Position>(() => {
    const stored = loadPosition();
    return stored || defaultPosition;
  });

  const [isDragging, setIsDragging] = useState(false);

  // Track drag state without causing re-renders
  const dragStateRef = useRef({
    startX: 0,
    startY: 0,
    startPosX: 0,
    startPosY: 0,
    elementWidth: 0,
    elementHeight: 0,
  });

  // Handle mouse/touch move
  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging) return;

      const { startX, startY, startPosX, startPosY, elementWidth, elementHeight } = dragStateRef.current;

      // Calculate new position (inverted because we use right/bottom positioning)
      const deltaX = startX - clientX;
      const deltaY = startY - clientY;

      const newPosition = constrainToViewport(
        {
          x: startPosX + deltaX,
          y: startPosY + deltaY,
        },
        elementWidth,
        elementHeight
      );

      setPosition(newPosition);
    },
    [isDragging]
  );

  // Handle drag end
  const handleEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      savePosition(position);
      onDragEnd?.(position);
    }
  }, [isDragging, position, onDragEnd]);

  // Mouse event handlers
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      handleEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMove, handleEnd]);

  // Touch event handlers
  useEffect(() => {
    if (!isDragging) return;

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleTouchEnd = () => {
      handleEnd();
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleTouchEnd);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isDragging, handleMove, handleEnd]);

  // Start drag from mouse
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled) return;

      const target = e.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();

      dragStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPosX: position.x,
        startPosY: position.y,
        elementWidth: rect.width,
        elementHeight: rect.height,
      };

      setIsDragging(true);
    },
    [enabled, position]
  );

  // Start drag from touch
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled || e.touches.length !== 1) return;

      const target = e.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const touch = e.touches[0];

      dragStateRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startPosX: position.x,
        startPosY: position.y,
        elementWidth: rect.width,
        elementHeight: rect.height,
      };

      setIsDragging(true);
    },
    [enabled, position]
  );

  // Reset to default position
  const resetPosition = useCallback(() => {
    setPosition(defaultPosition);
    savePosition(defaultPosition);
  }, [defaultPosition]);

  // Constrain position on mount and window resize
  useEffect(() => {
    const constrainPosition = () => {
      setPosition((prev) => {
        // Use estimated element size (actual size captured on drag start)
        const constrained = constrainToViewport(prev, 40, 40);
        if (constrained.x !== prev.x || constrained.y !== prev.y) {
          savePosition(constrained);
          return constrained;
        }
        return prev;
      });
    };

    // Constrain on mount
    constrainPosition();

    // Constrain on resize
    window.addEventListener('resize', constrainPosition);
    return () => window.removeEventListener('resize', constrainPosition);
  }, []);

  return {
    position,
    isDragging,
    dragProps: {
      onMouseDown: handleMouseDown,
      onTouchStart: handleTouchStart,
      style: {
        right: `${position.x}px`,
        bottom: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: isDragging ? 'none' : undefined,
        touchAction: 'none',
      },
    },
    resetPosition,
  };
}

export default useDraggable;
